import { energyRange, type WeekDay } from "@/api";
import { cn } from "@/lib/utils";

const W = 560;
const PAD = { top: 22, right: 16, bottom: 26, left: 16 };

function dayName(date: string): string {
  return new Date(`${date}T00:00`).toLocaleDateString([], { weekday: "short" });
}

function columns(days: WeekDay[]) {
  const colW = (W - PAD.left - PAD.right) / days.length;
  return days.map((d, i) => ({ d, cx: PAD.left + colW * i + colW / 2, colW }));
}

/**
 * The week in one chart on a shared 0–100% axis: each day's Energy Level as
 * a wide, muted background bar whose darker top section is the from–to band
 * (a single-value level is a plain bar), and the Day Score (as a share of
 * 10) as a line on top connecting every scored day.
 */
export function WeekChart({ days }: { days: WeekDay[] }) {
  const H = 190;
  const baseY = H - PAD.bottom;
  const plotH = baseY - PAD.top;
  const y = (pct: number) =>
    baseY - (Math.max(0, Math.min(100, pct)) / 100) * plotH;
  const cols = columns(days);
  const barW = Math.min(46, (cols[0]?.colW ?? 60) * 0.62);

  const points = cols
    .filter(({ d }) => d.score !== null)
    .map(({ d, cx }) => `${cx},${y(((d.score as number) / 10) * 100)}`)
    .join(" ");

  return (
    <figure className="m-0">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        role="img"
        aria-label={`Per day: ${days
          .map(
            (d) =>
              `${d.date} score ${d.score ?? "none"}, energy ${d.energy_level ? energyRange(d.energy_level) : "none"}`,
          )
          .join("; ")}`}
      >
        {[50, 100].map((g) => (
          <line
            key={g}
            x1={PAD.left}
            x2={W - PAD.right}
            y1={y(g)}
            y2={y(g)}
            className="stroke-border"
            strokeWidth={1}
            strokeDasharray="2 4"
          />
        ))}
        {/* Background bars: Energy Level (capacity band). */}
        {cols.map(({ d, cx }) => {
          if (!d.energy_level) return null;
          const pct = d.energy_level.percent;
          const min = Math.min(d.energy_level.percent_min, pct);
          const range = energyRange(d.energy_level);
          return (
            <g key={`bar-${d.date}`}>
              <rect
                x={cx - barW / 2}
                y={y(pct)}
                width={barW}
                height={Math.max(2, baseY - y(pct))}
                rx={4}
                className="fill-primary/15"
              >
                <title>{`${d.date}: ${d.energy_level.name} ${range}`}</title>
              </rect>
              {min < pct && (
                <rect
                  x={cx - barW / 2}
                  y={y(pct)}
                  width={barW}
                  height={Math.max(2, y(min) - y(pct))}
                  rx={4}
                  className="fill-primary/25"
                >
                  <title>{`${d.date}: ${d.energy_level.name} band ${range}`}</title>
                </rect>
              )}
              <text
                x={cx}
                y={y(pct) + 12}
                textAnchor="middle"
                className="fill-muted-foreground text-[10px]"
              >
                {range}
              </text>
            </g>
          );
        })}
        <line
          x1={PAD.left}
          x2={W - PAD.right}
          y1={baseY}
          y2={baseY}
          className="stroke-border"
          strokeWidth={1.5}
        />
        {/* Line: Day Score. */}
        {points.includes(" ") && (
          <polyline
            points={points}
            fill="none"
            className="stroke-primary"
            strokeWidth={2.5}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        )}
        {cols.map(({ d, cx }) => (
          <g key={d.date}>
            {d.score !== null ? (
              <>
                <circle
                  cx={cx}
                  cy={y((d.score / 10) * 100)}
                  r={4.5}
                  className="fill-primary"
                  stroke="var(--card)"
                  strokeWidth={2}
                >
                  <title>{`${d.date}: Day Score ${d.score}/10`}</title>
                </circle>
                <text
                  x={cx}
                  y={y((d.score / 10) * 100) - 9}
                  textAnchor="middle"
                  className="fill-foreground text-[11px] font-semibold"
                >
                  {d.score}
                </text>
              </>
            ) : (
              !d.energy_level && (
                <circle cx={cx} cy={baseY} r={2.5} className="fill-border" />
              )
            )}
            <text
              x={cx}
              y={H - 6}
              textAnchor="middle"
              className={cn(
                "text-[11px]",
                d.is_today
                  ? "fill-foreground font-semibold"
                  : "fill-muted-foreground",
              )}
            >
              {dayName(d.date)}
            </text>
          </g>
        ))}
      </svg>
      <figcaption className="mt-1 flex flex-wrap gap-4 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-0.5 w-4 rounded bg-primary"
            aria-hidden
          />{" "}
          Day Score (of 10)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-2.5 w-3 rounded-sm bg-primary/15"
            aria-hidden
          />{" "}
          Energy Level (%, darker = from–to band)
        </span>
      </figcaption>
    </figure>
  );
}
